import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAudioPlayer from './useAudioPlayer';
import { useChapterManager } from './useChapterManager';
import { getAudioProgress, getEffectiveProgress } from '@/utils/audioProgressUtils';
import { resolveAssetUrl } from '@/utils/offlineAssets';
import { subscribeToNetworkState } from '@/utils/networkState';
import { useReaderBook } from './useReaderBook';
import { clearChapterRuntimeCache, setChapterRuntimePinned, useChapterAudio } from './useChapterAudio';
import { useSleepTimer } from './useSleepTimer';
import { usePlaybackSpeed } from './usePlaybackSpeed';
import { getReaderChapterVoice } from '@/services/booksApi';
import type {
  ReaderContentReadiness,
  ReaderPositionAcknowledgement,
  ReaderPositionRequest,
} from '@/types/readerReadiness';

interface AudioConfig {
  url: string;
}

interface UseAudioManagerProps {
  mainAudio?: AudioConfig;
  bookId: string;
  bookName: string;
  legacyBookKeys?: string[];
  onSleepTimerExpire?: (minutes: number) => void;
  onBookCompleted?: () => void;
  // True while the reader surface is on screen for this book, and therefore
  // the only time it can report content readiness. The docked player (any
  // other route, or a book docked at launch) has no text to stay in sync
  // with, so it must neither have its audio withheld nor wait on a reader
  // handshake that is never coming.
  isReaderAttached: boolean;
}

interface ChapterNavigationOptions {
  resumePlayback?: boolean;
  fragmentId?: string | null;
}

type PlaybackIntentReason = 'initial-autoplay' | 'resume' | 'sequential-advance' | 'seek' | 'voice-command' | 'chapter-select';

interface PlaybackIntent {
  requestId: number;
  chapterId: string;
  reason: PlaybackIntentReason;
  desiredPlay: boolean;
  seekSeconds?: number;
  fragmentId?: string | null;
  // `seekSeconds` is the saved position being put back on the element, not a
  // new one the user chose. Such a seek is never persisted, and until it lands
  // the reader positions from it instead of the element (pendingResumeSeconds).
  restoresSavedOffset?: boolean;
}

export const useAudioManager = ({
  mainAudio,
  bookId,
  bookName,
  legacyBookKeys = [],
  onSleepTimerExpire,
  onBookCompleted,
  isReaderAttached,
}: UseAudioManagerProps) => {
  // Determine any saved chapter for this book up front so we can jump there before audio loads.
  const savedProgress = useMemo(() => getAudioProgress(bookName, legacyBookKeys), [bookName, legacyBookKeys]);

  const {
    book: readerBook,
    isLoading: isLoadingBook,
    error: readerBookError,
    isRenderProfileSupported,
    retry: retryReaderBook,
  } = useReaderBook(bookId);

  const [voiceSelection, setVoiceSelection] = useState<{ bookId: string | null; voiceKey: string }>({
    bookId: null,
    voiceKey: '',
  });
  const selectedReadingVoiceKey =
    voiceSelection.bookId === readerBook?.id
      ? voiceSelection.voiceKey
      : readerBook?.defaultVoiceKey ?? '';

  useEffect(() => {
    clearChapterRuntimeCache();
    return () => clearChapterRuntimeCache();
  }, [readerBook?.id]);

  useEffect(() => {
    if (!readerBook) {
      setVoiceSelection({ bookId: null, voiceKey: '' });
      return;
    }
    setVoiceSelection({ bookId: readerBook.id, voiceKey: readerBook.defaultVoiceKey });
  }, [readerBook?.id, readerBook?.defaultVoiceKey]);

  // Chapter management
  const chapterManager = useChapterManager(readerBook, selectedReadingVoiceKey, savedProgress);
  const {
    currentChapter,
    currentChapterIndex,
    nextChapter: nextChapterInternal,
    previousChapter: previousChapterInternal,
    hasNextChapter,
    hasPreviousChapter,
    chapters,
    goToChapter: goToChapterInternal,
  } = chapterManager;

  // --- Target-scoped playback intent -------------------------------------
  // Every reason audio might start playing (initial autoplay, sequential
  // chapter advance, saved resume, cross-chapter seek, voice commands) funnels
  // through one intent, resolved only once the reader has confirmed both
  // content readiness and position readiness for the target chapter. This is
  // what lets a cross-chapter seek withhold the audio source and apply the
  // target offset before playback starts, instead of racing the reader's own
  // layout/word-sync.
  const intentRef = useRef<PlaybackIntent | null>(null);
  const nextIntentRequestIdRef = useRef(0);
  const appliedSeekRequestIdRef = useRef<number | null>(null);
  const savedOffsetAppliedRef = useRef(false);
  // The saved-offset restore currently in flight, until its seek lands. Scoped
  // to the intent rather than to "saved offset not applied yet": an intent that
  // displaces the restore (chapter selection, tap-to-seek) makes the element
  // authoritative again, and a stale value here would have the reader follow a
  // position the audio is no longer at.
  const [pendingRestore, setPendingRestore] = useState<{ chapterId: string; seconds: number } | null>(null);
  const didRequestSavedRestoreRef = useRef(false);
  const consumedAutoplayVersionRef = useRef(0);
  const [autoplayRequestVersion, setAutoplayRequestVersion] = useState(0);
  const [intentResolutionVersion, setIntentResolutionVersion] = useState(0);
  const [contentReadiness, setContentReadiness] = useState<ReaderContentReadiness | null>(null);
  const [positionRequest, setPositionRequest] = useState<ReaderPositionRequest | null>(null);
  const [positionAck, setPositionAck] = useState<ReaderPositionAcknowledgement | null>(null);

  const allocateRequestId = useCallback(() => {
    nextIntentRequestIdRef.current += 1;
    return nextIntentRequestIdRef.current;
  }, []);

  const cancelIntent = useCallback(() => {
    intentRef.current = null;
    setPendingRestore(null);
  }, []);

  const queueIntent = useCallback((intent: PlaybackIntent) => {
    intentRef.current = intent;
    setPendingRestore(
      intent.restoresSavedOffset && typeof intent.seekSeconds === 'number'
        ? { chapterId: intent.chapterId, seconds: intent.seekSeconds }
        : null,
    );
    setIntentResolutionVersion((version) => version + 1);
  }, []);

  const handleReaderContentReadinessChange = useCallback((readiness: ReaderContentReadiness | null) => {
    setContentReadiness(readiness);
  }, []);

  const handleReaderPositioned = useCallback((acknowledgement: ReaderPositionAcknowledgement | null) => {
    setPositionAck(acknowledgement);
  }, []);

  const requestAutoplay = useCallback(() => {
    setAutoplayRequestVersion((version) => version + 1);
  }, []);

  const {
    runtime: chapterRuntime,
    timingsStatus: chapterTimingsStatus,
  } = useChapterAudio(currentChapter, selectedReadingVoiceKey);
  const currentChapterVoice = useMemo(
    () => getReaderChapterVoice(currentChapter, selectedReadingVoiceKey),
    [currentChapter, selectedReadingVoiceKey],
  );

  // Calculate start time of current chapter (sum of previous chapters' durations)
  const chapterStartTime = useMemo(() => {
    if (!currentChapter || chapters.length === 0) {
      return 0;
    }

    const index = chapters.findIndex((chapter) => chapter.id === currentChapter.id);
    if (index === -1) {
      return 0;
    }

    return chapters.slice(0, index).reduce(
      (acc, chapter) => acc + (getReaderChapterVoice(chapter, selectedReadingVoiceKey)?.durationMs || 0) / 1000,
      0,
    );
  }, [currentChapter, chapters, selectedReadingVoiceKey]);

  // Determine current audio source (chapters take precedence over mainAudio).
  // Withhold the target chapter's audio until the reader has confirmed its
  // content is laid out for that same chapter, so navigation never lets audio
  // start before (or get ahead of) the text/highlighting is ready.
  //
  // The gate is one-way per chapter: once a chapter's source has been handed
  // over it stays attached, whatever readiness does afterwards. Readiness
  // drops to null every time the reader unmounts (navigating out of /read) or
  // remounts, and tearing the source down there would pause playback, drop
  // `src` and reset the element to t=0 — losing the listening position for a
  // reason that has nothing to do with staying ahead of the text.
  //
  // "Attached" includes where the source points. Whether a chapter's audio is
  // on this device is looked up once, when the chapter is handed over, and
  // the answer holds for as long as that chapter and voice are the ones
  // playing. The offline index changes under a playing chapter — the buffer
  // finishes fetching it, or a download is removed — and this selection
  // re-runs on every readiness change, so looking it up each time would swap
  // a streaming chapter onto its local file (or back) mid-listen, which is
  // the same teardown: pause, new `src`, t=0. The next chapter looks afresh.
  //
  // Both records belong to a book, and are cleared in the render that first
  // sees a different one — not in the book-change effect further down. An
  // effect runs after its render, and a book whose payload is already
  // cached hands its chapter over in that very render (as does a player
  // with no reader attached, which has nothing to wait for). Clearing
  // afterwards forgot a chapter that had just been released: the next time
  // this selection re-ran without readiness in hand — the reader attaching
  // to a docked player, say — it withheld a source that was attached and
  // playing, and looked its location up again.
  const releasedChapterIdRef = useRef<string | null>(null);
  const releasedSourceRef = useRef<{ chapterId: string; audioUrl: string; resolvedUrl: string } | null>(null);
  const releasedForBookIdRef = useRef(bookId);
  if (releasedForBookIdRef.current !== bookId) {
    releasedForBookIdRef.current = bookId;
    releasedChapterIdRef.current = null;
    releasedSourceRef.current = null;
  }
  const effectiveMainAudio = useMemo(() => {
    if (currentChapter) {
      const isReleased =
        releasedChapterIdRef.current === currentChapter.id ||
        contentReadiness?.chapterId === currentChapter.id ||
        !isReaderAttached;
      if (!isReleased) {
        return undefined;
      }
      releasedChapterIdRef.current = currentChapter.id;
      const audioUrl = currentChapterVoice?.audioUrl;
      if (!audioUrl) {
        return undefined;
      }
      const released = releasedSourceRef.current;
      if (released && released.chapterId === currentChapter.id && released.audioUrl === audioUrl) {
        return { url: released.resolvedUrl };
      }
      const resolvedUrl = resolveAssetUrl(audioUrl);
      releasedSourceRef.current = { chapterId: currentChapter.id, audioUrl, resolvedUrl };
      return { url: resolvedUrl };
    }

    return mainAudio;
  }, [contentReadiness, currentChapter, currentChapterVoice, isReaderAttached, mainAudio]);

  // Content readiness is the reader's to report, so with no reader attached
  // there is nothing to wait for: the docked player is free to play.
  const isContentReadyForChapter = useCallback(
    (chapterId: string) => !isReaderAttached || contentReadiness?.chapterId === chapterId,
    [contentReadiness, isReaderAttached],
  );

  // --- Handing a streaming chapter over to its copy on this device --------
  // The lookup above is made once per chapter, so a chapter that began as a
  // stream stays a stream even after the buffer, or a download, has put its
  // whole file on disk. Left at that, losing the connection mid-chapter
  // stops playback with the file sitting right there — the first chapter
  // after opening a book is always in this position.
  //
  // So the chapter is handed over to its file, deliberately, at the moments
  // it is free or needed: whenever playback is paused, when play is pressed,
  // when the connection drops, and when the element reports it has run out
  // of data. Never merely because the file has landed: while the stream is
  // healthy a hand-off would cost every listener a blip on the first
  // chapter of every book, to protect against something that may not
  // happen. The player carries the position and the play state across.
  const [sourceHandOff, setSourceHandOff] = useState<{ audioUrl: string; url: string } | null>(null);
  // The player's element, which does not exist yet at this point in the
  // render; filled in once the player hook below has returned it.
  const handOffAudioRef = useRef<HTMLAudioElement | null>(null);
  const considerSourceHandOff = useCallback(() => {
    const released = releasedSourceRef.current;
    if (!released) {
      return;
    }
    if (handOffAudioRef.current?.ended) {
      // A chapter that has played to its end has nothing left to protect,
      // and putting a new source back at the end position could announce
      // the end a second time — which is what advances to the next chapter.
      return;
    }
    const onDevice = resolveAssetUrl(released.audioUrl);
    if (onDevice === released.audioUrl || onDevice === released.resolvedUrl) {
      // Nothing on this device, or it is what the chapter started from.
      return;
    }
    setSourceHandOff((current) =>
      current?.audioUrl === released.audioUrl && current.url === onDevice
        ? current
        : { audioUrl: released.audioUrl, url: onDevice });
  }, []);
  // Only ever for the chapter it was decided for; a stale one is ignored.
  const sourceOverrideUrl =
    effectiveMainAudio && sourceHandOff && sourceHandOff.audioUrl === currentChapterVoice?.audioUrl
      ? sourceHandOff.url
      : null;

  // Use the existing audio player hook with dynamic audio
  const audioPlayerHook = useAudioPlayer(
    effectiveMainAudio?.url || '',
    chapterRuntime,
    bookName,
    legacyBookKeys,
    currentChapter?.id,
    chapterStartTime,
    !hasNextChapter,
    { sourceOverrideUrl, onSourceStarved: considerSourceHandOff },
  );

  const {
    audioRef,
    isPlaying,
    isReady,
    isPositionRestored,
    pause: pauseAudioPlayer,
    play: playAudioPlayer,
  } = audioPlayerHook;

  handOffAudioRef.current = audioRef.current;

  // Paused: a hand-off costs nothing the listener can hear.
  useEffect(() => {
    if (!isPlaying) {
      considerSourceHandOff();
    }
  }, [considerSourceHandOff, isPlaying]);

  // The connection dropped: the stream has only what it already buffered.
  useEffect(
    () => subscribeToNetworkState((state) => {
      if (!state.isOnline) {
        considerSourceHandOff();
      }
    }),
    [considerSourceHandOff],
  );

  // Speed + pitch correction ride on the same <audio> element the player owns.
  const { playbackRate, setPlaybackRate } = usePlaybackSpeed(audioRef);

  // Book-total listening position, for anything that reports where the user is
  // (analytics). Two things make `chapterStartTime + currentTime` the wrong
  // answer on its own: the element sits at 0 from the moment a chapter's src
  // attaches until a queued intent applies the saved offset, and before it is
  // ready there is no element position at all. Inputs are mirrored into a ref
  // so the accessor's identity never changes and a memoised consumer can never
  // call a stale copy of it.
  const bookProgressInputsRef = useRef({
    chapterStartTime,
    currentTime: audioPlayerHook.currentTime,
    isReady,
    savedProgress,
    currentChapterId: currentChapter?.id,
  });
  bookProgressInputsRef.current = {
    chapterStartTime,
    currentTime: audioPlayerHook.currentTime,
    isReady,
    savedProgress,
    currentChapterId: currentChapter?.id,
  };

  const getBookProgressSeconds = useCallback((): number => {
    const inputs = bookProgressInputsRef.current;
    const saved = inputs.savedProgress;
    const savedTotal = getEffectiveProgress(saved);

    // Nothing loaded to read a position from: where they left off is the
    // truthful answer, and it is what the player is about to resume to.
    if (!inputs.isReady) {
      return savedTotal;
    }

    const elementTime = audioRef.current?.currentTime ?? inputs.currentTime;

    // While a saved offset is still waiting to be applied the element reads 0,
    // which is not where the user is. Answer from the saved position instead:
    // against this chapter when it is the one loaded, and against the book
    // while the element has not moved off the start.
    if (!savedOffsetAppliedRef.current && saved && saved.timeOffset > 0) {
      if (inputs.currentChapterId && saved.chapterId === inputs.currentChapterId) {
        return inputs.chapterStartTime + saved.timeOffset;
      }
      if (elementTime === 0) {
        return savedTotal;
      }
    }

    return inputs.chapterStartTime + elementTime;
  }, [audioRef]);

  useEffect(() => {
    setChapterRuntimePinned(currentChapter, selectedReadingVoiceKey, isPlaying);
    return () => setChapterRuntimePinned(currentChapter, selectedReadingVoiceKey, false);
  }, [currentChapter, isPlaying, selectedReadingVoiceKey]);

  useEffect(() => {
    if (currentChapter && !currentChapterVoice) {
      cancelIntent();
    }
  }, [cancelIntent, currentChapter, currentChapterVoice]);

  // Reset all readiness/intent state whenever the book changes.
  useEffect(() => {
    cancelIntent();
    appliedSeekRequestIdRef.current = null;
    savedOffsetAppliedRef.current = false;
    didRequestSavedRestoreRef.current = false;
    consumedAutoplayVersionRef.current = 0;
    setAutoplayRequestVersion(0);
    setContentReadiness(null);
    setPositionRequest(null);
    setPositionAck(null);
    setSourceHandOff(null);
  }, [bookId, cancelIntent]);

  // The position handshake is the reader's, so a request/ack pair outlives its
  // owner the moment the reader detaches. Drop them rather than let a stale
  // request be replayed as a surprise scroll when the reader comes back; an
  // intent still in flight then asks the fresh reader again.
  useEffect(() => {
    if (isReaderAttached) {
      return;
    }
    setPositionRequest(null);
    setPositionAck(null);
  }, [isReaderAttached]);

  const play = useCallback(() => {
    if (!currentChapter) {
      playAudioPlayer();
      return;
    }
    // The file may have landed while paused, after the last look.
    considerSourceHandOff();
    const isCurrentReaderReady =
      isContentReadyForChapter(currentChapter.id) &&
      chapterTimingsStatus !== 'loading' &&
      isReady;
    const savedResumeSeconds =
      !savedOffsetAppliedRef.current &&
      savedProgress?.chapterId === currentChapter.id &&
      savedProgress.timeOffset > 0
        ? savedProgress.timeOffset
        : undefined;
    if (!isCurrentReaderReady || savedResumeSeconds !== undefined) {
      queueIntent({
        requestId: allocateRequestId(),
        chapterId: currentChapter.id,
        reason: 'resume',
        desiredPlay: true,
        seekSeconds: savedResumeSeconds,
        restoresSavedOffset: savedResumeSeconds !== undefined,
      });
      return;
    }
    cancelIntent();
    playAudioPlayer();
  }, [allocateRequestId, cancelIntent, chapterTimingsStatus, considerSourceHandOff, currentChapter, isContentReadyForChapter, isReady, playAudioPlayer, queueIntent, savedProgress]);

  const pause = useCallback(() => {
    cancelIntent();
    pauseAudioPlayer();
  }, [cancelIntent, pauseAudioPlayer]);

  const togglePlay = useCallback(() => {
    if (isPlaying || intentRef.current?.desiredPlay) {
      pause();
    } else {
      play();
    }
  }, [isPlaying, pause, play]);

  const handleSleepTimerExpire = useCallback((minutes: number) => {
    cancelIntent();
    onSleepTimerExpire?.(minutes);
  }, [cancelIntent, onSleepTimerExpire]);

  const {
    sleepTimer,
    remainingSeconds: sleepTimerRemainingSeconds,
    setCountdownSleepTimer,
    setEndOfChapterSleepTimer,
    cancelSleepTimer,
    expireCountdownIfDue,
  } = useSleepTimer({
    audioRef,
    pause,
    bookId,
    onExpire: handleSleepTimerExpire,
  });

  const effectiveDuration = currentChapterVoice?.durationMs ? currentChapterVoice.durationMs / 1000 : 0;

  // Chapter navigation functions
  const goToNextChapter = useCallback((options: ChapterNavigationOptions = {}): boolean => {
    if (!hasNextChapter) {
      return false;
    }
    const wasPlaying = isPlaying;
    const resumePlayback = options.resumePlayback ?? wasPlaying;
    if (wasPlaying) {
      pauseAudioPlayer();
    }
    const targetChapterId = chapters[currentChapterIndex + 1]?.id;
    if (nextChapterInternal()) {
      if (sleepTimer?.mode === 'end-of-chapter') {
        cancelSleepTimer();
      }
      if (resumePlayback && targetChapterId) {
        queueIntent({
          requestId: allocateRequestId(),
          chapterId: targetChapterId,
          reason: 'sequential-advance',
          desiredPlay: true,
        });
      } else {
        cancelIntent();
      }
      return true;
    }
    return false;
  }, [allocateRequestId, cancelIntent, cancelSleepTimer, chapters, currentChapterIndex, hasNextChapter, isPlaying, nextChapterInternal, pauseAudioPlayer, queueIntent, sleepTimer?.mode]);

  const goToPreviousChapter = useCallback((options: ChapterNavigationOptions = {}): boolean => {
    if (!hasPreviousChapter) {
      return false;
    }
    const wasPlaying = isPlaying;
    const resumePlayback = options.resumePlayback ?? wasPlaying;
    if (wasPlaying) {
      pauseAudioPlayer();
    }
    const targetChapterId = chapters[currentChapterIndex - 1]?.id;
    if (previousChapterInternal()) {
      if (sleepTimer?.mode === 'end-of-chapter') {
        cancelSleepTimer();
      }
      if (resumePlayback && targetChapterId) {
        queueIntent({
          requestId: allocateRequestId(),
          chapterId: targetChapterId,
          reason: 'sequential-advance',
          desiredPlay: true,
        });
      } else {
        cancelIntent();
      }
      return true;
    }
    return false;
  }, [allocateRequestId, cancelIntent, cancelSleepTimer, chapters, currentChapterIndex, hasPreviousChapter, isPlaying, pauseAudioPlayer, previousChapterInternal, queueIntent, sleepTimer?.mode]);

  const goToChapter = useCallback((index: number, options: ChapterNavigationOptions = {}): boolean => {
    if (index < 0 || index >= chapters.length) {
      return false;
    }
    if (index === currentChapterIndex) {
      return true;
    }

    const wasPlaying = isPlaying;
    const resumePlayback = options.resumePlayback ?? wasPlaying;
    if (wasPlaying) {
      pauseAudioPlayer();
    }
    const targetChapterId = chapters[index]?.id;
    const didNavigate = goToChapterInternal(index);
    if (didNavigate && targetChapterId) {
      queueIntent({
        requestId: allocateRequestId(),
        chapterId: targetChapterId,
        reason: 'chapter-select',
        desiredPlay: resumePlayback,
        seekSeconds: 0,
        fragmentId: options.fragmentId,
      });
    } else {
      cancelIntent();
    }
    if (didNavigate && sleepTimer?.mode === 'end-of-chapter') {
      cancelSleepTimer();
    }
    return didNavigate;
  }, [allocateRequestId, cancelIntent, cancelSleepTimer, chapters, currentChapterIndex, goToChapterInternal, isPlaying, pauseAudioPlayer, queueIntent, sleepTimer?.mode]);

  // Cross-chapter seek/play, shared by manual UI seeks and voice commands.
  // Owns navigation, withholding the source until the reader is ready for the
  // target chapter, applying the requested offset, and only then playing.
  const seekAndPlay = useCallback((time: number, targetChapterId?: string) => {
    if (!targetChapterId || targetChapterId === currentChapter?.id) {
      cancelIntent();
      audioPlayerHook.seek(time);
      play();
      return;
    }
    const targetIndex = chapters.findIndex((chapter) => chapter.id === targetChapterId);
    if (targetIndex < 0) {
      return;
    }
    const didNavigate = goToChapterInternal(targetIndex);
    if (!didNavigate) {
      return;
    }
    if (sleepTimer?.mode === 'end-of-chapter') {
      cancelSleepTimer();
    }
    queueIntent({
      requestId: allocateRequestId(),
      chapterId: targetChapterId,
      reason: 'seek',
      desiredPlay: true,
      seekSeconds: time,
    });
  }, [allocateRequestId, audioPlayerHook, cancelIntent, cancelSleepTimer, chapters, currentChapter?.id, goToChapterInternal, play, queueIntent, sleepTimer?.mode]);

  // Consume a requestAutoplay() call as soon as a chapter is available, making
  // it one-shot: the flag is retired the moment the intent is accepted, not
  // deferred to a later book-change effect.
  useEffect(() => {
    if (autoplayRequestVersion === consumedAutoplayVersionRef.current || !currentChapter) {
      return;
    }
    consumedAutoplayVersionRef.current = autoplayRequestVersion;
    const seekSeconds =
      !savedOffsetAppliedRef.current &&
      savedProgress && savedProgress.chapterId === currentChapter.id && savedProgress.timeOffset > 0
        ? savedProgress.timeOffset
        : undefined;
    queueIntent({
      requestId: allocateRequestId(),
      chapterId: currentChapter.id,
      reason: 'initial-autoplay',
      desiredPlay: true,
      seekSeconds,
      restoresSavedOffset: seekSeconds !== undefined,
    });
  }, [allocateRequestId, autoplayRequestVersion, currentChapter, queueIntent, savedProgress]);

  // The saved offset the element is about to be given, or null when the
  // element's own time is authoritative. The reader positions from this before
  // the audio element carries the offset — it has to, because the source is
  // withheld until the reader reports content readiness, and that readiness
  // includes having positioned itself.
  const pendingResumeSeconds =
    pendingRestore && pendingRestore.chapterId === currentChapter?.id ? pendingRestore.seconds : null;

  // Restoring a saved position is not playback, but until this ran it rode on
  // playback anyway: the offset reached the element only as a side effect of
  // resolving an intent, and a page load into the reader queues none (opening
  // a book must never start audio). So the element sat at 0 — the scrubber
  // read 0 from it, and the reader positioned itself on the chapter's first
  // word — until the first play() finally queued one. Queue a paused intent
  // instead, which walks the same handshake and simply doesn't play at the end.
  useEffect(() => {
    if (didRequestSavedRestoreRef.current || !currentChapter || savedOffsetAppliedRef.current) {
      return;
    }
    if (!savedProgress || savedProgress.chapterId !== currentChapter.id || savedProgress.timeOffset <= 0) {
      return;
    }
    // Never displace an intent already in flight: an autoplay queued for this
    // same load carries the same offset and is the one that should win.
    if (intentRef.current || isPlaying) {
      return;
    }
    didRequestSavedRestoreRef.current = true;
    queueIntent({
      requestId: allocateRequestId(),
      chapterId: currentChapter.id,
      reason: 'resume',
      desiredPlay: false,
      seekSeconds: savedProgress.timeOffset,
      restoresSavedOffset: true,
    });
  }, [allocateRequestId, currentChapter, isPlaying, queueIntent, savedProgress]);

  // Resolve the active intent once the reader confirms content readiness for
  // the target chapter and the audio element has metadata loaded: apply any
  // saved/seek offset, then wait for the reader to acknowledge it has
  // positioned itself before starting playback.
  useEffect(() => {
    const intent = intentRef.current;
    if (!intent || !currentChapter || currentChapter.id !== intent.chapterId) {
      return;
    }
    if (!isContentReadyForChapter(intent.chapterId)) {
      return;
    }
    if (chapterTimingsStatus === 'loading' || !isReady) {
      return;
    }
    if (expireCountdownIfDue()) {
      cancelIntent();
      return;
    }

    if (typeof intent.seekSeconds === 'number' && appliedSeekRequestIdRef.current !== intent.requestId) {
      const isSavedOffsetRestore = intent.restoresSavedOffset === true;
      // A restore puts the element where the saved position already says the
      // user is, so it must not be written back (see useAudioPlayer.seek).
      audioPlayerHook.seek(intent.seekSeconds, { persist: !isSavedOffsetRestore });
      appliedSeekRequestIdRef.current = intent.requestId;
      // The element now carries the position itself.
      setPendingRestore(null);
      if (isSavedOffsetRestore) {
        savedOffsetAppliedRef.current = true;
      }
    }

    // Positioning the text is the reader's half of the handshake; docked,
    // there is nobody to ask and nothing to scroll, so the intent resolves
    // straight after the seek.
    if (isReaderAttached) {
      if (positionRequest?.chapterId !== intent.chapterId || positionRequest.requestId !== intent.requestId) {
        setPositionRequest({
          chapterId: intent.chapterId,
          requestId: intent.requestId,
          mode: intent.reason === 'chapter-select' ? 'chapter-heading' : 'audio-time',
          fragmentId: intent.fragmentId ?? null,
        });
        return;
      }

      if (positionAck?.chapterId !== intent.chapterId || positionAck.requestId !== intent.requestId) {
        return;
      }
    }

    intentRef.current = null;
    appliedSeekRequestIdRef.current = null;
    if (intent.desiredPlay) {
      playAudioPlayer();
    }
  }, [
    audioPlayerHook,
    cancelIntent,
    chapterTimingsStatus,
    currentChapter,
    expireCountdownIfDue,
    intentResolutionVersion,
    isContentReadyForChapter,
    isReaderAttached,
    isReady,
    playAudioPlayer,
    positionAck,
    positionRequest,
  ]);

  // Listen for audio end events
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const handleEnded = () => {
      if (!hasNextChapter) {
        onBookCompleted?.();
      }

      if (expireCountdownIfDue()) {
        return;
      }

      if (sleepTimer?.mode === 'end-of-chapter') {
        cancelIntent();
        cancelSleepTimer();
        if (hasNextChapter) {
          nextChapterInternal();
        }
        return;
      }

      if (hasNextChapter) {
        const targetChapterId = chapters[currentChapterIndex + 1]?.id;
        if (nextChapterInternal() && targetChapterId) {
          queueIntent({
            requestId: allocateRequestId(),
            chapterId: targetChapterId,
            reason: 'sequential-advance',
            desiredPlay: true,
          });
        }
      }
    };

    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('ended', handleEnded);
    };
  }, [
    allocateRequestId,
    audioRef,
    cancelIntent,
    cancelSleepTimer,
    chapters,
    currentChapterIndex,
    expireCountdownIfDue,
    hasNextChapter,
    nextChapterInternal,
    onBookCompleted,
    queueIntent,
    sleepTimer?.mode,
  ]);

  const bookInfo = useMemo(
    () =>
      readerBook
        ? {
            id: readerBook.id,
            title: readerBook.title,
            author: readerBook.author,
            notes: readerBook.description || '',
            subheading: readerBook.subtitle || undefined,
          }
        : null,
    [readerBook]
  );

  return {
    ...audioPlayerHook,
    play,
    pause,
    togglePlay,
    duration: effectiveDuration,
    currentAudioUrl: effectiveMainAudio?.url || '',
    totalCurrentTime: audioPlayerHook.currentTime + chapterStartTime,
    getBookProgressSeconds,
    ...chapterManager,
    nextChapter: goToNextChapter,
    previousChapter: goToPreviousChapter,
    goToChapter,
    goToNextChapter,
    goToPreviousChapter,
    seekAndPlay,
    requestAutoplay,
    pendingResumeSeconds,
    positionRequest,
    onReaderContentReadinessChange: handleReaderContentReadinessChange,
    onReaderPositioned: handleReaderPositioned,
    sleepTimer,
    sleepTimerRemainingSeconds,
    setCountdownSleepTimer,
    setEndOfChapterSleepTimer,
    cancelSleepTimer,
    playbackRate,
    setPlaybackRate,
    bookInfo,
    isPositionRestored,
    readerBook,
    isLoadingBook,
    readerBookError,
    retryReaderBook,
    isRenderProfileSupported,
    chapterRuntime,
    chapterTimingsStatus,
    selectedReadingVoiceKey,
    currentChapterVoice,
  };
};
